import React, { useState, useEffect, useMemo } from 'react';
import { format, isPast } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import AddInstallmentModal from '../Modals/AddInstallmentModal';
import EditInstallmentModal from '../Modals/EditInstallmentModal';
import {
  CreditCard,
  Search,
  Filter,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  Euro,
  Calendar,
  User,
  FileText,
  Edit,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface Installment {
  id: string;
  contract_id: string;
  student_id: string;
  branch_id: string;
  installment_number: number;
  amount: number;
  currency: string;
  due_date: string;
  payment_date: string | null;
  status: string;
  payment_method: string | null;
  notes: string | null;
  contracts: {
    contract_number: string;
  };
  students: {
    first_name: string;
    last_name: string;
  };
}

const Installments: React.FC = () => {
  const { user } = useAuth();
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedInstallment, setSelectedInstallment] = useState<Installment | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'bank_transfer',
    notes: ''
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [expandedContracts, setExpandedContracts] = useState<string[]>([]);

  const handleDelete = async (id: string) => {
    if (!await window.appConfirm('Bu taksiti silmek istediğinizden emin misiniz?')) return;

    try {
      const { error } = await supabase
        .from('installments')
        .delete()
        .eq('id', id);

      if (error) throw error;
      alert('Taksit başarıyla silindi');
      fetchInstallments();
    } catch (err: any) {
      console.error('Error deleting installment:', err);
      alert('Silme işlemi sırasında hata oluştu: ' + err.message);
    }
  };

  const handleEdit = (installment: Installment) => {
    setSelectedInstallment(installment);
    setShowEditModal(true);
  };

  useEffect(() => {
    fetchInstallments();
  }, [statusFilter]);

  const fetchInstallments = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('installments')
        .select(`
          *,
          contracts (
            contract_number
          )
        `)
        .order('due_date', { ascending: true });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const isAccounting = user?.role?.toLowerCase() === 'accounting' ||
        (user as any)?.unitType?.toLowerCase() === 'accounting' ||
        (user?.role?.toLowerCase() === 'unit' && (user as any)?.unitType?.toLowerCase() === 'accounting');
      const isAdmin = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'super_admin';

      if (user && !isAdmin && !isAccounting) {
        query = query.eq('branch_id', user.branchId);
      }

      const { data, error } = await query;
      if (error) throw error;

      const rawInstallments = data || [];
      const studentIds = [...new Set(rawInstallments.map(i => i.student_id).filter(Boolean))];

      let studentsMap = new Map();
      if (studentIds.length > 0) {
        const { data: studentsData } = await supabase.from('students').select('id, first_name, last_name').in('id', studentIds);
        if (studentsData) {
          studentsMap = new Map(studentsData.map(s => [s.id, s]));
        }
      }

      const updatedData = rawInstallments.map(inst => {
        const studentInfo = inst.student_id ? studentsMap.get(inst.student_id) : null;

        const mappedInst = {
          ...inst,
          students: studentInfo ? {
            first_name: studentInfo.first_name || '',
            last_name: studentInfo.last_name || ''
          } : { first_name: 'Bilinmeyen', last_name: 'Öğrenci' }
        };

        if (mappedInst.status === 'pending' && isPast(new Date(mappedInst.due_date))) {
          return { ...mappedInst, status: 'overdue' };
        }
        return mappedInst;
      });

      setInstallments(updatedData as any);
    } catch (err) {
      console.error('Error fetching installments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!selectedInstallment) return;

    try {
      const { error } = await supabase
        .from('installments')
        .update({
          status: 'paid',
          payment_date: paymentData.payment_date,
          payment_method: paymentData.payment_method,
          notes: paymentData.notes || null
        })
        .eq('id', selectedInstallment.id);

      if (error) throw error;

      alert('Taksit ödemesi başarıyla kaydedildi');
      setShowPaymentModal(false);
      setSelectedInstallment(null);
      setPaymentData({
        payment_date: format(new Date(), 'yyyy-MM-dd'),
        payment_method: 'bank_transfer',
        notes: ''
      });
      fetchInstallments();
    } catch (err) {
      console.error('Error updating installment:', err);
      alert('Taksit güncellenirken hata oluştu');
    }
  };

  const getStatusBadge = (status: string, dueDate: string) => {
    let config = {
      label: 'Beklemede',
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      icon: Clock
    };

    if (status === 'paid') {
      config = {
        label: 'Ödendi',
        color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
        icon: CheckCircle
      };
    } else if (status === 'overdue' || (status === 'pending' && isPast(new Date(dueDate)))) {
      config = {
        label: 'Gecikmiş',
        color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
        icon: AlertCircle
      };
    } else if (status === 'cancelled') {
      config = {
        label: 'İptal',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
        icon: XCircle
      };
    }

    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const filteredInstallments = installments.filter(inst => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (inst.contracts?.contract_number || '').toLowerCase().includes(searchLower) ||
      (inst.students?.first_name || '').toLowerCase().includes(searchLower) ||
      (inst.students?.last_name || '').toLowerCase().includes(searchLower)
    );
  });

  const groupedInstallments = useMemo(() => {
    const groups: {
      [key: string]: {
        contractId: string,
        contractNumber: string,
        studentName: string,
        installments: Installment[],
        totalAmount: number,
        paidAmount: number,
        currency: string
      }
    } = {};

    filteredInstallments.forEach(inst => {
      const key = inst.contract_id;
      if (!groups[key]) {
        groups[key] = {
          contractId: inst.contract_id,
          contractNumber: inst.contracts?.contract_number || 'Bilinmiyor',
          studentName: inst.students ? `${inst.students.first_name} ${inst.students.last_name}` : 'Bilinmiyor',
          installments: [],
          totalAmount: 0,
          paidAmount: 0,
          currency: inst.currency
        };
      }
      groups[key].installments.push(inst);
      groups[key].totalAmount += Number(inst.amount);
      if (inst.status === 'paid') {
        groups[key].paidAmount += Number(inst.amount);
      }
    });

    return Object.values(groups);
  }, [filteredInstallments]);

  const toggleContract = (contractId: string) => {
    setExpandedContracts(prev =>
      prev.includes(contractId)
        ? prev.filter(id => id !== contractId)
        : [...prev, contractId]
    );
  };

  const stats = {
    total: installments.length,
    paid: installments.filter(i => i.status === 'paid').length,
    pending: installments.filter(i => i.status === 'pending').length,
    overdue: installments.filter(i => i.status === 'overdue' || (i.status === 'pending' && isPast(new Date(i.due_date)))).length,
    totalAmount: installments.reduce((sum, i) => sum + Number(i.amount), 0),
    paidAmount: installments.filter(i => i.status === 'paid').reduce((sum, i) => sum + Number(i.amount), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Taksit Takibi</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Öğrenci taksit ödemelerini takip edin
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          <Plus className="w-5 h-5" />
          Taksit Planı Oluştur
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Toplam Taksit</p>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 mt-1">{stats.total}</p>
            </div>
            <CreditCard className="w-10 h-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 dark:text-green-400 font-medium">Ödenen</p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100 mt-1">{stats.paid}</p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">€{stats.paidAmount.toLocaleString()}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-4 border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">Bekleyen</p>
              <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100 mt-1">{stats.pending}</p>
            </div>
            <Clock className="w-10 h-10 text-yellow-500" />
          </div>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-200 dark:border-red-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">Gecikmiş</p>
              <p className="text-2xl font-bold text-red-900 dark:text-red-100 mt-1">{stats.overdue}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Sözleşme numarası veya öğrenci adı ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tüm Durumlar</option>
          <option value="pending">Bekleyen</option>
          <option value="paid">Ödenen</option>
          <option value="overdue">Gecikmiş</option>
          <option value="cancelled">İptal</option>
        </select>
      </div>

      {groupedInstallments.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
          <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Taksit bulunamadı</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {searchTerm || statusFilter !== 'all' ? 'Arama kriterlerinize uygun taksit bulunamadı.' : 'Henüz taksit eklenmemiş.'}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-10"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Sözleşme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Öğrenci
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Toplam Tutar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ödenen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Kalan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Taksit Sayısı
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {groupedInstallments.map((group) => {
                  const isExpanded = expandedContracts.includes(group.contractId);
                  const remainingAmount = group.totalAmount - group.paidAmount;

                  return (
                    <React.Fragment key={group.contractId}>
                      <tr
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer ${isExpanded ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
                        onClick={() => toggleContract(group.contractId)}
                      >
                        <td className="px-6 py-4 text-center">
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                            {group.contractNumber}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 dark:text-white font-medium">
                            {group.studentName}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {group.currency} {group.totalAmount.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-green-600 dark:text-green-400">
                            {group.currency} {group.paidAmount.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-red-600 dark:text-red-400">
                            {group.currency} {remainingAmount.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                            {group.installments.length} Taksit
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} className="px-0 py-0 border-b border-gray-200 dark:border-gray-700">
                            <div className="bg-gray-50 dark:bg-gray-900/50 p-4">
                              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm">
                                <thead className="bg-gray-100 dark:bg-gray-900">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Taksit No</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Vade Tarihi</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tutar</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ödeme Tarihi</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Durum</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">İşlemler</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {group.installments.map(inst => (
                                    <tr key={inst.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        #{inst.installment_number}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`text-sm ${isPast(new Date(inst.due_date)) && inst.status !== 'paid'
                                          ? 'text-red-600 dark:text-red-400 font-semibold'
                                          : 'text-gray-600 dark:text-gray-400'
                                          }`}>
                                          {format(new Date(inst.due_date), 'dd MMM yyyy', { locale: tr })}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                        {inst.currency} {inst.amount.toLocaleString()}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {inst.payment_date ? format(new Date(inst.payment_date), 'dd MMM yyyy', { locale: tr }) : '-'}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {getStatusBadge(inst.status, inst.due_date)}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex gap-2">
                                          {inst.status !== 'paid' && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedInstallment(inst);
                                                setShowPaymentModal(true);
                                              }}
                                              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium"
                                            >
                                              Öde
                                            </button>
                                          )}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEdit(inst);
                                            }}
                                            className="text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 text-sm font-medium"
                                          >
                                            <Edit className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDelete(inst.id);
                                            }}
                                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showPaymentModal && selectedInstallment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Ödeme Kaydet
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ödeme Tarihi
                </label>
                <input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ödeme Yöntemi
                </label>
                <select
                  value={paymentData.payment_method}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="cash">Nakit</option>
                  <option value="credit_card">Kredi Kartı</option>
                  <option value="bank_transfer">Banka Transferi</option>
                  <option value="check">Çek</option>
                  <option value="other">Diğer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Not (Opsiyonel)
                </label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  placeholder="Ödeme ile ilgili notlar..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleMarkAsPaid}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium"
                >
                  Ödemeyi Kaydet
                </button>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedInstallment(null);
                    setPaymentData({
                      payment_date: format(new Date(), 'yyyy-MM-dd'),
                      payment_method: 'bank_transfer',
                      notes: ''
                    });
                  }}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-medium"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AddInstallmentModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={fetchInstallments}
      />

      <EditInstallmentModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={fetchInstallments}
        installment={selectedInstallment}
      />
    </div>
  );
};

export default Installments;
